import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClientTable = ({ 
  clients, 
  onClientEdit, 
  onClientView,
  onClientDelete,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSort,
  selectedClients = [],
  onSelectionChange
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const isAllSelected = clients?.length > 0 && selectedClients.length === clients.length;
  const isPartiallySelected = selectedClients.length > 0 && selectedClients.length < clients?.length;

  const handleSelectAll = (e) => {
    onSelectionChange(e.target.checked ? clients.map(c => c.id) : []);
  };

  const handleSelectOne = (id, checked) => {
    onSelectionChange(checked ? [...selectedClients, id] : selectedClients.filter(s => s !== id));
  };

  const toggleRowExpansion = (clientId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(clientId)) {
      newExpanded?.delete(clientId);
    } else {
      newExpanded?.add(clientId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-success text-success-foreground';
      case 'Inactive': return 'bg-muted text-muted-foreground';
      case 'Pending': return 'bg-warning text-warning-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
      {/* Table Header with Search */}
      <div className="p-6 border-b border-border bg-gradient-to-r from-muted/30 to-muted/10">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-2xl">
            <Input
              type="search"
              placeholder="Search clients by name, email, or contact person..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e?.target?.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full table-auto">
          <thead className="bg-muted/50 border-b-2 border-border">
            <tr>
              <th className="px-6 py-4 w-12">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="text-left px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Client Name & Code</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="text-left px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">Branch</th>
              <th className="text-left px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">Contact</th>
              <th className="text-left px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">Contact Person</th>
              <th className="text-left px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">Bank Vertical</th>
              <th className="text-center px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients?.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <Icon name="Users" size={48} color="var(--color-muted-foreground)" />
                    <p className="text-muted-foreground text-lg">No clients found</p>
                    <p className="text-sm text-muted-foreground">Add your first client to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              clients?.map((client) => (
                <React.Fragment key={client?.id}>
                  <tr className="border-b border-border hover:bg-muted/20 transition-all duration-150">
                    <td className="px-6 py-5">
                      <Checkbox
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => handleSelectOne(client.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center space-x-3">
                        {/* <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-primary">
                            {client?.name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)?.toUpperCase()}
                          </span>
                        </div> */}
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground">{client?.name}</div>
                          <div className="text-xs text-muted-foreground">{client?.client_code || 'No Code'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="font-medium text-foreground">{client?.branch || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="text-foreground text-sm">{client?.email || 'N/A'}</div>
                      <div className="text-xs text-muted-foreground mt-1">{client?.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="font-medium text-foreground">{client?.contact_person || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="font-medium text-foreground">{client?.bank_vertical || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleRowExpansion(client?.id)}
                          title="View details"
                        >
                          <Icon 
                            name={expandedRows?.has(client?.id) ? 'ChevronUp' : 'ChevronDown'} 
                            size={16} 
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onClientEdit(client)}
                          title="Edit client"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onClientDelete(client)}
                          title="Delete client"
                          className="hover:text-destructive"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRows?.has(client?.id) && (
                  <tr className="bg-muted/20">
                    <td colSpan="7" className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Client Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Address:</span>
                              <span className="text-foreground text-right">{client?.address || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">GST Number:</span>
                              <span className="text-foreground">{client?.gst_number || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Client Code:</span>
                              <span className="text-foreground">{client?.client_code || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Bank Information</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Bank Vertical:</span>
                              <span className="text-foreground">{client?.bank_vertical || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Request ID:</span>
                              <span className="text-foreground">{client?.bank_request_id || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden">
        {clients?.map((client) => (
          <div key={client?.id} className="border-b border-border p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {client?.name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-foreground">{client?.name}</div>
                  <div className="text-sm text-muted-foreground">{client?.branch}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleRowExpansion(client?.id)}
                >
                  <Icon 
                    name={expandedRows?.has(client?.id) ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Contact</div>
                <div className="text-foreground">{client?.email}</div>
                <div className="text-foreground">{client?.phone}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Contact Person</div>
                <div className="text-foreground font-medium">{client?.contact_person || 'N/A'}</div>
              </div>
            </div>
            
            <div className="mt-3 text-sm">
              <div className="text-muted-foreground">Bank Vertical</div>
              <div className="text-foreground">{client?.bank_vertical || 'N/A'}</div>
            </div>
            
            {expandedRows?.has(client?.id) && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Client Details</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <div className="text-muted-foreground">Address</div>
                        <div className="text-foreground">{client?.address || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">GST Number</div>
                        <div className="text-foreground">{client?.gst_number || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Bank Request ID</div>
                        <div className="text-foreground">{client?.bank_request_id || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" iconName="Edit" iconPosition="left" onClick={() => onClientEdit(client)}>
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" iconName="Trash2" iconPosition="left" onClick={() => onClientDelete(client)} className="text-destructive hover:bg-destructive/10">
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClientTable;